import { Card, ExternalLink } from '@ui/themed';
import { t } from 'i18next';
import { PageSection } from '../../../components/page-section';
import { isShopify, platformLabel, useEnv } from '../../../context/env-context';
import { useSettings } from '../../../context/settings-context';
import { platformProductUrl } from '../../../lib/platform-links';
import { I18N_COMPONENTS } from '../constants';
import { findImage, findMetaDataEntry } from '../product-metadata';
import type { Product } from '../types/product';
import { DetailGrid } from './detail-grid';
import { ProductImage } from './product-image';

export function ProductParentCard({ product }: { product: Product }) {
  const env = useEnv();
  const { useProductsV2 } = useSettings();

  const platform = env.connected_platform;
  const externalId = product.identifiers.platform ?? '';

  const productUrl = isShopify(platform)
    ? (platformProductUrl(platform, env.store_id, externalId) ?? '')
    : (product.links?.admin_url ?? '');

  const showExternalLink = isShopify(platform) || productUrl !== '';

  const productImage = findImage(product, platform);

  const options = product.option_groups
    .map((group) => {
      const metadata = findMetaDataEntry(
        product.metadata,
        group.metadata_namespace,
        group.metadata_key,
      );
      return metadata ? String(metadata.value) : undefined;
    })
    .filter(Boolean)
    .join(', ');

  const details: Record<string, string> = {
    [t(`${I18N_COMPONENTS}.parent_card.product_name`)]: product.title,
    [t(`${I18N_COMPONENTS}.parent_card.id_slug`)]: product.slug,
    [t(`${I18N_COMPONENTS}.parent_card.external_id`)]: externalId,
  };
  if (product.identifiers.internal) {
    details[t(`${I18N_COMPONENTS}.parent_card.internal_id`)] = product.identifiers.internal;
  }
  if (options) {
    details[t(`${I18N_COMPONENTS}.parent_card.product_options`)] = options;
  }

  const platformName = platformLabel(platform);

  return (
    <PageSection
      title={t(`${I18N_COMPONENTS}.parent_card.title`)}
      action={
        showExternalLink && platformName ? (
          <ExternalLink link={productUrl} showIcon>
            {t(`${I18N_COMPONENTS}.parent_card.view_in_platform`, { platform: platformName })}
          </ExternalLink>
        ) : undefined
      }
    >
      <Card>
        <Card.Body>
          <div className="flex items-center gap-x-6">
            {useProductsV2 && <ProductImage src={productImage} size="lg" />}
            <DetailGrid details={details} />
          </div>
        </Card.Body>
      </Card>
    </PageSection>
  );
}
